import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../../components/ui/textarea";
import { FullPageLoader } from "../../components/RouteGuards";
import { WizardCtx } from "../../components/register/WizardContext";
import StepReview from "../../components/register/steps/StepReview";
import { supabase } from "../../lib/supabase";
import { mergeDraft } from "../../lib/formData";
import { followupBadge, followupLabel, setDraftFollowup, FOLLOWUP_STATUSES } from "../../lib/draftApi";
import { staffDirectory, fmtDateTime } from "../../lib/adminApi";

const NP = (v) => v || "Not provided";

function ContactCard({ row }) {
  const items = [
    { label: "Mobile / contact", value: row.applicant_mobile || row.present_phone, href: (v) => `tel:${v}`, Icon: Phone, testId: "detail-call" },
    { label: "WhatsApp", value: row.present_whatsapp || row.applicant_mobile || row.present_phone, href: (v) => `https://wa.me/91${v}`, Icon: MessageCircle, testId: "detail-whatsapp" },
    { label: "Email", value: row.email, href: (v) => `mailto:${v}`, Icon: Mail, testId: "detail-email" },
  ];
  return (
    <div data-testid="draft-contact-card" className="rmm-card grid gap-3 p-5 sm:grid-cols-3">
      {items.map(({ label, value, href, Icon, testId }) => (
        <div key={label} className="rounded-lg border border-[#D4AF37]/35 bg-[#FAFAF7] p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#8C6A6C]">{label}</p>
          {value ? (
            <a href={href(value)} target={label === "WhatsApp" ? "_blank" : undefined} rel="noreferrer" data-testid={testId} className="mt-1 inline-flex items-center gap-1.5 font-semibold text-[#7A0016] hover:underline">
              <Icon className="h-4 w-4" /> {value}
            </a>
          ) : (
            <p className="mt-1 text-sm text-[#8C6A6C]">Not provided</p>
          )}
        </div>
      ))}
    </div>
  );
}

function FollowupPanel({ row, reload }) {
  const [status, setStatus] = useState(row.follow_up_status);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [staff, setStaff] = useState({});
  useEffect(() => { staffDirectory().then(setStaff); }, []);
  const save = async () => {
    setBusy(true);
    try { await setDraftFollowup(row.user_id, status, note.trim() || null); toast.success("Follow-up saved"); setNote(""); reload(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <section className="rmm-card p-5" data-testid="followup-panel">
      <h3 className="font-display text-2xl font-semibold text-[#7A0016]">Follow-up</h3>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="text-xs font-medium text-[#8C6A6C]">Status
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="followup-status-select" className="rmm-select rmm-input mt-1 h-10 w-56 text-[#1A1515]">
            {FOLLOWUP_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <button type="button" onClick={save} disabled={busy} data-testid="followup-save-button" className="rounded-full bg-[#7A0016] px-5 py-2 text-sm font-semibold text-[#F5E6BA] hover:bg-[#4A000E] disabled:opacity-50">Save follow-up</button>
      </div>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note (optional)" data-testid="followup-note-input" className="mt-3" />
      <ul className="mt-4 space-y-2">
        {(row.follow_up_notes || []).slice().reverse().map((n, i) => (
          <li key={i} data-testid={`followup-note-${i}`} className="rounded-lg border border-[#D4AF37]/30 bg-[#FAFAF7] p-3 text-sm">
            {n.note}
            <span className="mt-1 block text-xs text-[#8C6A6C]">{staff[n.by] || "Staff"} · {fmtDateTime(n.at)}</span>
          </li>
        ))}
        {row.followed_up_at && (
          <p className="text-xs text-[#8C6A6C]">Last follow-up by {staff[row.followed_up_by] || "staff"} on {fmtDateTime(row.followed_up_at)} — <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${followupBadge(row.follow_up_status)}`}>{followupLabel(row.follow_up_status)}</span></p>
        )}
      </ul>
    </section>
  );
}

export default function IncompleteDetail() {
  const { userId } = useParams();
  const [row, setRow] = useState(null);
  const load = useCallback(async () => {
    const { data } = await supabase.from("incomplete_registrations").select("*").eq("user_id", userId).maybeSingle();
    setRow(data || false);
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const ctx = useMemo(() => row && ({ data: mergeDraft(row.data), set: () => {}, errors: {}, userId: null, mode: "admin", goTo: () => {} }), [row]);
  if (row === null) return <FullPageLoader />;
  if (row === false) return <p data-testid="draft-not-found" className="rmm-card p-8 text-sm text-[#8C6A6C]">Registration not found or already submitted.</p>;
  return (
    <div data-testid="incomplete-detail-page" className="space-y-6">
      <Link to="/admin/profiles/incomplete" data-testid="back-to-incomplete" className="inline-flex items-center gap-1 text-sm text-[#8C6A6C] hover:text-[#7A0016]"><ArrowLeft className="h-4 w-4" /> Incomplete registrations</Link>
      <div className="rmm-card p-5 sm:p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Incomplete registration · <span className="capitalize">{row.signup_method === "google" ? "Google" : "Email"} signup</span></p>
        <h1 className="mt-1 font-display text-3xl font-bold text-[#4A000E] sm:text-4xl">{NP(row.full_name)}</h1>
        <p className="mt-2 text-sm text-[#8C6A6C]">
          Signed up {fmtDateTime(row.signed_up_at)} · Last activity {fmtDateTime(row.last_activity_at)} · Reached <strong data-testid="draft-step">Step {row.current_step} of 8</strong> ({Math.round((row.current_step / 8) * 100)}% complete)
          {row.profile_for && <> · Registering for a <span className="capitalize">{row.profile_for}</span></>}
        </p>
      </div>
      <ContactCard row={row} />
      <FollowupPanel row={row} reload={load} />
      <section>
        <h3 className="mb-3 font-display text-2xl font-semibold text-[#7A0016]">Details entered so far</h3>
        <WizardCtx.Provider value={ctx}><StepReview emptyText="Not provided" /></WizardCtx.Provider>
      </section>
    </div>
  );
}
